import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { alpha, useTheme } from '@mui/material/styles';

import { getPlatformIconUrl } from 'src/utils/platform-icon';

import SocialProof from 'src/components/social-proof';
import { MoneyBackPromise } from 'src/components/pricing/pricing-guarantee';

import ReviewStack from './review-stack';

// ----------------------------------------------------------------------

const MARKETPLACES = [
  { name: 'eBay', platform: 'ebay' },
  { name: 'Poshmark', platform: 'poshmark' },
  { name: 'Depop', platform: 'depop' },
  { name: 'Etsy', platform: 'etsy' },
  { name: 'Shopify', platform: 'shopify' },
  { name: 'WooCommerce', platform: 'wooCommerce' },
  { name: 'BigCommerce', platform: 'bigCommerce' },
];

// ----------------------------------------------------------------------

// The register page's left side (desktop): what Voolist does, in the
// website's words, then the reasons to trust it: the reseller count,
// customers' reviews (a stack that shuffles on its own), the money-back
// guarantee and the marketplaces it connects. The reviews are the one large
// element.
export default function RegisterAside () {
  const theme = useTheme();

  return (
    <Stack
      component="aside"
      flexGrow={1}
      justifyContent="center"
      sx={{
        px: { md: 5, lg: 8 },
        // Clear of the logo at the top left
        pt: 12,
        pb: 4,
        bgcolor: alpha(theme.palette.primary.main, 0.06),
        borderRight: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
      }}
    >
      <Box sx={{ width: 1, maxWidth: 520, mx: 'auto' }}>
        <Typography
          component="h2"
          sx={{
            fontWeight: 800,
            fontSize: { md: '2rem', lg: '2.25rem' },
            lineHeight: 1.15,
            letterSpacing: '-0.02em',
            textWrap: 'balance',
          }}
        >
          Sell on More Platforms Without the Extra Work
        </Typography>

        <Typography sx={{ mt: 1.5, color: 'text.secondary', fontSize: '1rem', lineHeight: 1.6 }}>
          List once on eBay, Poshmark, Depop, Etsy and your own store. When something sells, Voolist takes it
          down everywhere else in seconds.
        </Typography>

        <SocialProof align="left" sx={{ mt: 2.5 }} />

        <Box sx={{ mt: 3 }}>
          <ReviewStack />
        </Box>

        <MoneyBackPromise sx={{ mt: 2.5 }} />

        {/* Left out on narrower or shorter screens, so the panel still fits without scrolling */}
        <Box
          sx={{
            display: { md: 'none', lg: 'block' },
            '@media (max-height: 740px)': { display: 'none' },
          }}
        >
          <Stack direction="row" flexWrap="wrap" alignItems="center" sx={{ mt: 3, gap: 1.25 }}>
            <Typography sx={{ mr: 0.75, fontWeight: 600, fontSize: '0.875rem', color: 'text.secondary' }}>
              Works with
            </Typography>
            {MARKETPLACES.map(({ name, platform }) => (
              <Box
                key={name}
                title={name}
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: 1.25,
                  display: 'grid',
                  placeItems: 'center',
                  bgcolor: 'background.paper',
                  border: `1px solid ${alpha(theme.palette.grey[500], 0.16)}`,
                }}
              >
                <Box component="img" src={getPlatformIconUrl(platform)} alt={name} width={26} height={26} sx={{ objectFit: 'contain' }} />
              </Box>
            ))}
          </Stack>
        </Box>
      </Box>
    </Stack>
  );
}
